import React from 'react';
import {
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  Quote,
  ShieldCheck,
  Target,
} from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '@/components/ui/ScrollReveal';
import { Container, Section } from '@/components/layout/Container';
import { Button } from '@/components/ui/Button';
import { useBookingCallToAction } from '@/hooks/useBookingCallToAction';

const logoItems = [
  {
    src: '/assets/proof/logo-insurer.svg',
    label: 'Global insurer',
  },
  {
    src: '/assets/proof/logo-evac.svg',
    label: 'Medical evacuation operator',
  },
  {
    src: '/assets/proof/logo-medtech.svg',
    label: 'MedTech scale-up',
  },
  {
    src: '/assets/proof/logo-privatecare.svg',
    label: 'Private care group',
  },
  {
    src: '/assets/proof/logo-riskfirm.svg',
    label: 'Risk assessment firm',
  },
];

const measurableOutcomes = [
  {
    metric: '38%',
    label: 'Faster clinical risk triage decisions',
  },
  {
    metric: '31%',
    label: 'Reduction in duplicated documentation time',
  },
  {
    metric: '4/4',
    label: 'Programs passed governance review without critical findings',
  },
  {
    metric: '48h',
    label: 'Typical turnaround on executive advisory requests',
  },
];

const caseStudies = [
  {
    title: 'Cross-Border Risk Triage',
    clientType: 'Global Insurer (NDA)',
    challenge:
      'Escalation path between medical reviewers and operations teams was inconsistent across regions.',
    intervention:
      'Designed a standard triage and handoff model with explicit clinical thresholds and decision owners.',
    outcome:
      'Reduced high-severity response latency and improved handoff quality in first 90 days.',
  },
  {
    title: 'AI Workflow Safety Layer',
    clientType: 'Health Product Team (NDA)',
    challenge:
      'Engineering and clinical teams disagreed on acceptable guardrails for AI-generated recommendations.',
    intervention:
      'Defined a clinical validation layer, escalation matrix, and test protocol before release.',
    outcome:
      'Enabled production rollout with clear governance and stronger stakeholder confidence.',
  },
  {
    title: 'Private System Throughput',
    clientType: 'Private Care Group (NDA)',
    challenge:
      'Clinical leadership lacked visibility into bottlenecks between intake, assessment, and follow-up.',
    intervention:
      'Mapped process flow end-to-end and introduced KPI tracking tied to staffing and queue design.',
    outcome:
      'Improved throughput predictability and reduced avoidable wait-time spikes.',
  },
];

const testimonials = [
  {
    quote:
      'He translates high-stakes clinical complexity into decisions our engineering and leadership teams can execute immediately.',
    source: 'Chief Product Officer, MedTech Client (NDA)',
  },
  {
    quote:
      'The advisory work gave us a governance model that works under pressure, not only in slide decks.',
    source: 'Program Lead, International Risk Operations (NDA)',
  },
];

export const Proof: React.FC = () => {
  const { directBookingEnabled, triggerBooking } = useBookingCallToAction(import.meta.env.VITE_BOOKING_URL);
  const bookingLabel = directBookingEnabled ? 'Book Intro Call' : 'Request Intro Call';

  return (
    <Section id="proof" className="pt-12 md:pt-16">
      <Container>
        <ScrollReveal className="text-center mb-12">
          <span className="text-caption text-apple-blue uppercase tracking-wider font-medium">
            Proof and Outcomes
          </span>
          <h2 className="text-h1 md:text-display-m font-bold mt-4 mb-5">
            Trusted in <span className="gradient-text">High-Stakes</span> Programs
          </h2>
          <p className="text-body text-text-secondary max-w-3xl mx-auto">
            Representative clients and outcomes below are anonymized where required by NDA.
            The focus is measurable execution under clinical, operational, and governance pressure.
          </p>
        </ScrollReveal>

        <ScrollReveal>
          <div className="grid grid-cols-1 min-[520px]:grid-cols-2 xl:grid-cols-5 gap-3 mb-10">
            {logoItems.map((item) => (
              <div
                key={item.label}
                className="rounded-2xl border border-border-subtle bg-bg-tertiary p-3 flex items-center justify-center"
              >
                <img
                  src={item.src}
                  alt={item.label}
                  loading="lazy"
                  className="w-full h-auto rounded-xl"
                />
              </div>
            ))}
          </div>
        </ScrollReveal>

        <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {measurableOutcomes.map((item) => (
            <StaggerItem key={item.label}>
              <div className="h-full rounded-2xl border border-border-subtle bg-bg-tertiary p-5">
                <div className="flex items-center gap-2 mb-4 text-apple-blue">
                  <Target className="w-4 h-4" />
                  <span className="text-caption uppercase tracking-wider">Measured</span>
                </div>
                <div className="text-[clamp(1.8rem,5vw,2.6rem)] leading-none font-bold text-text-primary mb-3">
                  {item.metric}
                </div>
                <p className="text-body-s text-text-secondary">{item.label}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <StaggerContainer className="grid lg:grid-cols-3 gap-5 mb-10">
          {caseStudies.map((study) => (
            <StaggerItem key={study.title}>
              <div className="h-full rounded-2xl border border-border-subtle bg-bg-tertiary p-5 md:p-6">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <p className="text-caption uppercase tracking-wider text-apple-blue">
                    {study.clientType}
                  </p>
                  <ShieldCheck className="w-4 h-4 text-apple-blue" />
                </div>
                <h3 className="text-h4 font-semibold mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-caption uppercase tracking-wider text-text-tertiary mb-1">
                      Challenge
                    </p>
                    <p className="text-body-s text-text-secondary">{study.challenge}</p>
                  </div>
                  <div>
                    <p className="text-caption uppercase tracking-wider text-text-tertiary mb-1">
                      Intervention
                    </p>
                    <p className="text-body-s text-text-secondary">{study.intervention}</p>
                  </div>
                  <div>
                    <p className="text-caption uppercase tracking-wider text-text-tertiary mb-1">
                      Outcome
                    </p>
                    <p className="text-body-s text-text-primary">{study.outcome}</p>
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <StaggerContainer className="grid md:grid-cols-2 gap-4 mb-10">
          {testimonials.map((item) => (
            <StaggerItem key={item.source}>
              <div className="h-full rounded-2xl border border-border-subtle bg-bg-tertiary p-5">
                <Quote className="w-5 h-5 text-apple-blue mb-3" />
                <p className="text-body text-text-secondary mb-4">{item.quote}</p>
                <p className="text-caption uppercase tracking-wider text-text-tertiary">
                  {item.source}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <ScrollReveal>
          <div className="rounded-2xl border border-border-subtle bg-bg-tertiary p-6 md:p-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div>
              <p className="text-h4 font-semibold text-text-primary mb-2">
                Need a fast strategic read on your current program?
              </p>
              <p className="text-body-s text-text-secondary flex items-center gap-2">
                <Clock3 className="w-4 h-4 text-apple-blue" />
                Initial response target is within 48 hours for qualified requests.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="primary" href="#contact" icon={<CheckCircle2 className="w-4 h-4" />}>
                Start an Inquiry
              </Button>
              <Button
                variant="secondary"
                onClick={triggerBooking}
                icon={<ArrowUpRight className="w-4 h-4" />}
              >
                {bookingLabel}
              </Button>
            </div>
          </div>
        </ScrollReveal>
      </Container>
    </Section>
  );
};

export default Proof;
